import React, { useMemo, useState } from "react";

import {
  Box,
  /* , Button */
} from "@chakra-ui/react";
// import { API_ERROR_MESSAGES } from "api/axios/types";
import { IBusinessDetailDocument } from "api/business-user-management/types";
// import { resetFaceId } from "api/compliance";
// import { ReactComponent as RefreshIcon } from "assets/icons/refresh-icon.svg";
// import { useBusinessCompliance } from "contexts/business-compliance-context";
import { useApproveOrRejectBusinessSection } from "hooks/useApproveOrRejectBusinessSection";
import { formatDateToDDMMYYTTSS } from "utils";
import { REGISTERED_BUSINESS_COMPANY_TYPE } from "utils/constants";
import { Maybe } from "utils/general/maybe";

// import { customErrorToast, customSuccessToast } from "ui/custom-toast";
import { ImageViewer } from "ui/image-viewer";
import { Paragraph3 } from "ui/typography";

import { ApproveBusinessSectionModal } from "./ApproveBusinessSectionModal";
import { BusinessDirectorInformation } from "./BusinessDirectorInformation";
import { unRegisteredBusinessDirectorDocsLabel } from "./BusinessDocuments";
import { BusinessSectionAction } from "./BusinessSectionAction";
import { RejectBusinessSectionModal } from "./RejectBusinessSectionModal";
import { IBusinessDirectorsInformationProps } from "./types";

const START_INDEX = 0;
const INDEX_OFFSET = 1;
const NOT_FOUND_INDEX = -1;

export type TDOBFormat = "DD-MM-YYYY" | "YYYY-MM-DD";
export const detectDOBFormat = (dob: string): TDOBFormat => {
  const ddmmyyyyRegex = /^\d{2}-\d{2}-\d{4}$/; // Matches "19-02-1988"
  const yyyymmddRegex = /^\d{4}-\d{2}-\d{2}$/; // Matches "1975-12-19"

  if (ddmmyyyyRegex.test(dob)) {
    return "DD-MM-YYYY";
  } else if (yyyymmddRegex.test(dob)) {
    return "YYYY-MM-DD";
  }
  return "YYYY-MM-DD";
};

export const formatDateOfBirth = (dob: string) => {
  const dobFormat = detectDOBFormat(dob);
  let formattedDob = dob;
  if (dobFormat === "DD-MM-YYYY") {
    formattedDob = dob?.split("-").reverse().join("-");
  }
  const dateString = formatDateToDDMMYYTTSS(formattedDob);
  const index = dateString.indexOf("at");
  return index !== NOT_FOUND_INDEX
    ? dateString.slice(START_INDEX, index).trim()
    : dateString;
};

const getUnRegisteredBusinessDirectorDoc = (
  businessDocs: IBusinessDetailDocument[]
): IBusinessDetailDocument => {
  const validUnRegisteredBusinessDocs = businessDocs.filter(({ name }) =>
    Object.keys(unRegisteredBusinessDirectorDocsLabel).includes(name)
  );
  return validUnRegisteredBusinessDocs[
    validUnRegisteredBusinessDocs.length - INDEX_OFFSET
  ];
};
export const BusinessDirectorsInformation = (
  props: IBusinessDirectorsInformationProps
) => {
  const {
    business,
    status,
    isContentWrapperBelow745,
    isContentWrapperBelow600,
    isContentWrapperBelow450,
  } = props;
  // const { handleFetchBusinessDetails } = useBusinessCompliance();
  const [showRejectionReasonModal, setShowRejectionReasonModal] =
    useState(false);
  const [showApprovalSectionModal, setShowApprovalSectionModal] =
    useState(false);
  const [showIdDoc, setShowIdDoc] = useState(false);

  const handleToggleApproveOrRejectModal = () => {
    setShowApprovalSectionModal(false);
    setShowRejectionReasonModal(false);
  };

  const {
    approveBusinessSection: approveDirectorsInformation,
    rejectBusinessSection: rejectDirectorsInformation,
    isApprovingOrRejecting,
  } = useApproveOrRejectBusinessSection({
    businessId: business?.business?.id,
    businessSectionTitle: "Director's Information",
    handleToggleApproveOrRejectModal,
  });
  /* const [isResetting, setIsResetting] = useState(false);

  const handleResetFaceId = async () => {
    setIsResetting(true);
    try {
      const response = await resetFaceId({
        businessId: business?.business?.id,
      });

      if (response?.error) {
        customErrorToast({
          title: "Face ID Reset",
          message: response?.message || API_ERROR_MESSAGES.NETWORK_ERROR,
        });
      } else {
        customSuccessToast({
          title: "Face ID Reset",
          message: response?.message || "Face ID Reset Successful",
        });
        await handleFetchBusinessDetails(business?.business?.id);
      }
    } catch (error) {
      console.error(error);
    }
    setIsResetting(false);
  }; */

  const handleCloseIdDoc = () => {
    setShowIdDoc(false);
  };

  const handleToggleRejectionReasonModal = () => {
    setShowRejectionReasonModal(!showRejectionReasonModal);
  };

  const handleToggleApprovalSectionModal = () => {
    setShowApprovalSectionModal(!showApprovalSectionModal);
  };

  const businessDirectors = useMemo(
    () => business?.business?.companyData?.businessDirectors || [],
    [business]
  );

  const unRegisteredBusinessDoc = getUnRegisteredBusinessDirectorDoc(
    business?.business?.companyData?.businessDocuments || []
  );

  const isDirectorsInformationCtaDisabled = business?.business?.companyData
    ?.directorsInformationStatus
    ? business?.business?.companyData?.directorsInformationStatus?.status !==
      "pending"
    : !!business?.business?.companyData?.directorsInformationStatus;

  return (
    <Box>
      <Maybe
        condition={
          business?.business?.companyData?.companyType ===
          REGISTERED_BUSINESS_COMPANY_TYPE
        }
      >
        <Box className="flex flex-col gap-4">
          {businessDirectors.length > START_INDEX ? (
            businessDirectors.map((businessDirector, index) => (
              <BusinessDirectorInformation
                key={index}
                businessDirector={businessDirector}
                isContentWrapperBelow745={isContentWrapperBelow745}
                isContentWrapperBelow600={isContentWrapperBelow600}
                position={index + INDEX_OFFSET}
                business={business}
              />
            ))
          ) : (
            <Box>
              <Paragraph3
                lineHeight="22.4px"
                textColor="#718096"
              >
                No Business Directors
              </Paragraph3>
            </Box>
          )}
        </Box>
      </Maybe>
      {status === "review" &&
        business?.business?.companyData?.companyType ===
          REGISTERED_BUSINESS_COMPANY_TYPE && (
          <BusinessSectionAction
            isRejectSectionDisabled={isDirectorsInformationCtaDisabled}
            isApproveSectionDisabled={isDirectorsInformationCtaDisabled}
            handleAddRejectionReason={handleToggleRejectionReasonModal}
            handleApproveSection={handleToggleApprovalSectionModal}
            isContentWrapperBelow450={isContentWrapperBelow450}
          />
        )}
      {/* {status === "review" &&
        business?.business?.companyData?.companyType !==
          REGISTERED_BUSINESS_COMPANY_TYPE && (
          <Box className="mt-4 flex justify-end">
            <Button
              className={`flex items-center bg-white h-[41.57px] rounded-[7.43px] gap-[7.43px] ${isResetting || !business?.business?.companyData?.livenessCheck ? "cursor-not-allowed w-[130px]" : "cursor-pointer w-[100.86px]"}`}
              disabled={
                isResetting || !business?.business?.companyData?.livenessCheck
              }
              css={{
                border: "0.93px solid #D0D5DD",
              }}
              onClick={handleResetFaceId}
            >
              <RefreshIcon className={`${isResetting ? "animate-spin" : ""}`} />
              <span className="leading-[22.29px] text-[14px] text-[#00535C] font-semibold">
                {isResetting ? "Resetting..." : "Reset"}
              </span>
            </Button>
          </Box>
        )} */}
      <ApproveBusinessSectionModal
        isOpen={showApprovalSectionModal}
        setIsOpen={setShowApprovalSectionModal}
        handleToggleApprovalSectionModal={handleToggleApprovalSectionModal}
        handleApproveBusinessSection={approveDirectorsInformation}
        isLoading={isApprovingOrRejecting}
      />
      <RejectBusinessSectionModal
        isOpen={showRejectionReasonModal}
        setIsOpen={setShowRejectionReasonModal}
        handleToggleRejectSectionModal={handleToggleRejectionReasonModal}
        handleRejectBusinessSection={rejectDirectorsInformation}
        isLoading={isApprovingOrRejecting}
      />
      <ImageViewer
        imageDataList={[
          {
            title: "ID Document",
            imageUrl: unRegisteredBusinessDoc?.fileUrl || "",
          },
        ]}
        isVisible={showIdDoc}
        imageIndex={START_INDEX}
        handleCloseImageViewer={handleCloseIdDoc}
        uploaderName={`${business?.business?.firstName} ${business?.business?.lastName}`}
      />
    </Box>
  );
};
